import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import CallCenterModal from "./listing/CallCenterModal";
import * as Yup from "yup";
import PhoneNumberFieldArray from "./PhoneNumberFieldArray";
import {
  generateAccountPlanPreview,
  joinMobileNumbers,
  parseMobileNumbers,
  resolveTotalAmount,
  splitTotalAmount,
} from "../../utils/accountPlanSchedule";

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50";
const TEXTAREA_CLASS =
  "w-full min-h-[92px] px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 resize-y";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide";

export const EMPTY_ACCOUNT_PLAN_FORM = {
  planTypeId: "",
  accountNo: "",
  mobileNumbers: [""],
  totalAmount: "",
  frequency: "MONTHLY",
  installmentCount: "12",
  startDate: "",
  assignmentMode: "AUTO",
  assignedTo: "",
  reminderDaysBefore: "0",
  ticketPriority: "MEDIUM",
  remarksTemplate: "",
};

export const applyPlanTypeToForm = (planType, setFieldValue) => {
  if (!planType) return;
  setFieldValue("frequency", planType.frequency || "MONTHLY");
  setFieldValue("installmentCount", String(planType.installmentCount || "12"));
  setFieldValue("assignmentMode", planType.assignmentMode || "AUTO");
  setFieldValue(
    "assignedTo",
    planType.defaultAssignedTo ? String(planType.defaultAssignedTo) : ""
  );
  setFieldValue("reminderDaysBefore", String(planType.reminderDaysBefore ?? 0));
  setFieldValue("ticketPriority", planType.ticketPriority || "MEDIUM");
  setFieldValue("remarksTemplate", planType.remarksTemplate || "");
};

export const buildAccountPlanInitialValues = (plan) => {
  if (!plan) {
    return { ...EMPTY_ACCOUNT_PLAN_FORM, mobileNumbers: [""] };
  }
  return {
    planTypeId: plan.planTypeId || "",
    accountNo: plan.accountNo || "",
    mobileNumbers: parseMobileNumbers(plan.mobileNumbers),
    totalAmount: resolveTotalAmount(plan),
    frequency: plan.frequency || "MONTHLY",
    installmentCount: String(plan.installmentCount || ""),
    startDate: plan.startDate || "",
    assignmentMode: plan.assignmentMode || "AUTO",
    assignedTo: plan.assignedTo ? String(plan.assignedTo) : "",
    reminderDaysBefore: String(plan.reminderDaysBefore ?? 0),
    ticketPriority: plan.ticketPriority || "MEDIUM",
    remarksTemplate: plan.remarksTemplate || "",
  };
};

export const buildAccountPlanPayload = (values) => {
  const base = {
    accountNo: values.accountNo.trim(),
    mobileNumbers: joinMobileNumbers(values.mobileNumbers) || null,
    totalAmount: Number(values.totalAmount),
    startDate: values.startDate,
  };

  if (values.planTypeId) {
    return {
      ...base,
      planTypeId: values.planTypeId,
      assignedTo:
        values.assignmentMode === "MANUAL" && values.assignedTo
          ? Number(values.assignedTo)
          : null,
    };
  }

  return {
    ...base,
    frequency: values.frequency,
    installmentCount: values.frequency === "ONE_TIME" ? 1 : Number(values.installmentCount),
    assignmentMode: values.assignmentMode,
    assignedTo: values.assignmentMode === "MANUAL" ? Number(values.assignedTo) : null,
    reminderDaysBefore: Number(values.reminderDaysBefore),
    ticketPriority: values.ticketPriority,
    remarksTemplate: values.remarksTemplate.trim() || null,
  };
};

const buildValidationSchema = (t, isEditing, hasPlanTypes) => {
  const sharedShape = {
    assignmentMode: Yup.string().required(),
    assignedTo: Yup.string().when("assignmentMode", {
      is: "MANUAL",
      then: (schema) => schema.trim().required(t("ccAccountPlanAssignedAgentRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    reminderDaysBefore: Yup.string().required(),
    ticketPriority: Yup.string().required(t("ccPriorityRequired")),
    mobileNumbers: Yup.array().of(Yup.string()),
    remarksTemplate: Yup.string(),
  };

  if (isEditing) {
    return Yup.object(sharedShape);
  }

  return Yup.object({
    ...sharedShape,
    planTypeId: hasPlanTypes
      ? Yup.string().trim().required(t("ccAccountPlanTypeRequired"))
      : Yup.string(),
    accountNo: Yup.string().trim().required(t("ccAccountNumberRequired")),
    totalAmount: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("ccInvalidNumber"))
      .min(0.001, t("ccAccountPlanAmountMin"))
      .required(t("ccAccountPlanTotalAmountRequired")),
    frequency: Yup.string().when("planTypeId", {
      is: (value) => !value,
      then: (schema) => schema.required(t("ccAccountPlanFrequencyRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    installmentCount: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError(t("ccInvalidNumber"))
      .when("planTypeId", {
        is: (value) => !value,
        then: (schema) =>
          schema.when("frequency", {
            is: "ONE_TIME",
            then: (inner) =>
              inner
                .oneOf([1], t("ccAccountPlanInstallmentCountRequired"))
                .required(t("ccAccountPlanInstallmentCountRequired")),
            otherwise: (inner) =>
              inner
                .min(1, t("ccAccountPlanInstallmentCountRequired"))
                .max(120, t("ccAccountPlanInstallmentCountMax"))
                .required(t("ccAccountPlanInstallmentCountRequired")),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    startDate: Yup.string().trim().required(t("ccAccountPlanStartDateRequired")),
  });
};

const FormField = ({ label, required, error, touched, className = "", children }) => (
  <div className={className}>
    <label className={LABEL_CLASS}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
    {error && touched ? <p className="mt-1 text-xs text-red-500">{error}</p> : null}
  </div>
);

const fieldErrorClass = (error, touched) => (error && touched ? "border-red-500" : "");

const formatPreviewAmount = (amount) => Number(amount).toFixed(3);

const AccountPlanFormModal = ({
  open,
  editingPlan,
  metadata,
  saving,
  reminderOptions,
  agentOptions,
  onClose,
  onSubmit,
  t,
}) => {
  const isEditing = Boolean(editingPlan);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const initialValues = useMemo(
    () => buildAccountPlanInitialValues(editingPlan),
    [editingPlan, open]
  );
  const planTypes = metadata?.planTypes || [];
  const validationSchema = useMemo(
    () => buildValidationSchema(t, isEditing, !isEditing && planTypes.length > 0),
    [t, isEditing, planTypes.length]
  );

  useEffect(() => {
    if (!open) {
      setPreviewRows([]);
      setPreviewVisible(false);
    }
  }, [open]);

  const handlePreview = async (values, validateForm, setTouched) => {
    const errors = await validateForm();
    const previewFields = ["totalAmount", "installmentCount", "startDate", "frequency"];
    const hasPreviewErrors = previewFields.some((field) => errors[field]);
    if (hasPreviewErrors) {
      setTouched(
        previewFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
        true
      );
      setPreviewVisible(false);
      setPreviewRows([]);
      return;
    }

    const rows = generateAccountPlanPreview({
      totalAmount: values.totalAmount,
      frequency: values.frequency,
      installmentCount: values.installmentCount,
      startDate: values.startDate,
      reminderDaysBefore: values.reminderDaysBefore,
    });
    setPreviewRows(rows);
    setPreviewVisible(true);
  };

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      blockClose={saving}
      ariaLabelledBy="account-plan-form-title"
      eyebrow={isEditing ? t("ccAccountPlanEditTitle") : t("ccAccountPlanCreateTitle")}
      title={isEditing ? t("ccAccountPlanEditTitle") : t("ccAccountPlanCreateTitle")}
      subtitle={!isEditing ? t("ccAccountPlansPageSubtitle") : undefined}
      maxWidth="2xl"
      maxHeight="90vh"
      closeLabel={t("ccClose")}
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 overflow-hidden p-0"
    >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {({ values, errors, touched, setFieldValue, validateForm, setTouched }) => {
              const usesPlanType = Boolean(values.planTypeId);
              const isOneTimePlan = values.frequency === "ONE_TIME";
              const installmentCount = isOneTimePlan ? 1 : Number(values.installmentCount);
              const splitPreview = splitTotalAmount(values.totalAmount, installmentCount);
              const averageInstallment =
                splitPreview.length > 0 ? splitPreview[0] : null;
              const templateLocked = !isEditing && usesPlanType;

              return (
                <Form className="flex min-h-0 flex-1 flex-col">
                  <h3 id="account-plan-form-title" className="sr-only">
                    {isEditing ? t("ccAccountPlanEditTitle") : t("ccAccountPlanCreateTitle")}
                  </h3>
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
                    <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                      {!isEditing && planTypes.length > 0 ? (
                        <FormField
                          label={t("ccAccountPlanType")}
                          required
                          error={errors.planTypeId}
                          touched={touched.planTypeId}
                          className="md:col-span-2"
                        >
                          <Field
                            as="select"
                            name="planTypeId"
                            className={`${FIELD_CLASS} ${fieldErrorClass(errors.planTypeId, touched.planTypeId)}`}
                            onChange={(event) => {
                              const nextId = event.target.value;
                              setFieldValue("planTypeId", nextId);
                              const selected = planTypes.find((item) => item.id === nextId);
                              applyPlanTypeToForm(selected, setFieldValue);
                              setPreviewVisible(false);
                            }}
                          >
                            <option value="">{t("ccAccountPlanSelectType")}</option>
                            {planTypes.map((planType) => (
                              <option key={planType.id} value={planType.id}>
                                {planType.name}
                              </option>
                            ))}
                          </Field>
                          <p className="mt-1 text-xs text-slate-500">{t("ccAccountPlanTypeSelectHint")}</p>
                        </FormField>
                      ) : null}

                      <FormField
                        label={t("ccAccountPlanAccount")}
                        required={!isEditing}
                        error={errors.accountNo}
                        touched={touched.accountNo}
                        className="md:col-span-2"
                      >
                        <Field
                          name="accountNo"
                          disabled={isEditing}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.accountNo, touched.accountNo)}`}
                        />
                      </FormField>

                      <div className="md:col-span-2">
                        <label className={LABEL_CLASS}>{t("ccAccountPlanMobileNumbers")}</label>
                        <FieldArray name="mobileNumbers">
                          {({ form }) => (
                            <PhoneNumberFieldArray
                              values={form.values.mobileNumbers}
                              onChange={(next) => form.setFieldValue("mobileNumbers", next)}
                              addLabel={t("ccAddMobileNumber")}
                              removeLabel={t("ccRemoveMobileNumber")}
                              placeholder={t("ccMobileNumberPlaceholder")}
                              hint={t("ccMobileNumberHint")}
                              fieldLabelPrefix={t("ccMobileNumber")}
                              showFieldLabels
                              t={t}
                            />
                          )}
                        </FieldArray>
                      </div>

                      <FormField
                        label={t("ccAccountPlanTotalAmount")}
                        required={!isEditing}
                        error={errors.totalAmount}
                        touched={touched.totalAmount}
                      >
                        <Field
                          name="totalAmount"
                          type="number"
                          min="0.001"
                          step="0.001"
                          disabled={isEditing}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.totalAmount, touched.totalAmount)}`}
                        />
                        {!isEditing && averageInstallment != null && (
                          <p className="mt-1 text-xs text-slate-500">
                            {t("ccAccountPlanPerInstallmentHint").replace(
                              "{amount}",
                              formatPreviewAmount(averageInstallment)
                            )}
                          </p>
                        )}
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanFrequency")}
                        required={!isEditing}
                        error={errors.frequency}
                        touched={touched.frequency}
                      >
                        <Field
                          as="select"
                          name="frequency"
                          disabled={isEditing || templateLocked}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.frequency, touched.frequency)}`}
                          onChange={(event) => {
                            const nextFrequency = event.target.value;
                            setFieldValue("frequency", nextFrequency);
                            if (nextFrequency === "ONE_TIME") {
                              setFieldValue("installmentCount", "1");
                            }
                            setPreviewVisible(false);
                          }}
                        >
                          {(metadata?.frequencies || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanInstallmentCount")}
                        required={!isEditing}
                        error={errors.installmentCount}
                        touched={touched.installmentCount}
                      >
                        <Field
                          name="installmentCount"
                          type="number"
                          min="1"
                          max={isOneTimePlan ? 1 : 120}
                          disabled={isEditing || isOneTimePlan || templateLocked}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.installmentCount, touched.installmentCount)}`}
                          onChange={(event) => {
                            setFieldValue("installmentCount", event.target.value);
                            setPreviewVisible(false);
                          }}
                        />
                        {isOneTimePlan && !isEditing && (
                          <p className="mt-1 text-xs text-slate-500">{t("ccAccountPlanOneTimeNote")}</p>
                        )}
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanStartDate")}
                        required={!isEditing}
                        error={errors.startDate}
                        touched={touched.startDate}
                      >
                        <Field
                          name="startDate"
                          type="date"
                          disabled={isEditing}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.startDate, touched.startDate)}`}
                          onChange={(event) => {
                            setFieldValue("startDate", event.target.value);
                            setPreviewVisible(false);
                          }}
                        />
                      </FormField>

                      <FormField label={t("ccAccountPlanAssignmentMode")}>
                        <Field
                          as="select"
                          name="assignmentMode"
                          disabled={templateLocked}
                          className={FIELD_CLASS}
                        >
                          {(metadata?.assignmentModes || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      <FormField label={t("ccAccountPlanReminderOffset")}>
                        <Field
                          as="select"
                          name="reminderDaysBefore"
                          disabled={templateLocked}
                          className={FIELD_CLASS}
                        >
                          {reminderOptions.map((option) => (
                            <option key={option.daysBefore} value={String(option.daysBefore)}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      {values.assignmentMode === "MANUAL" && (
                        <FormField
                          label={t("ccAccountPlanAssignedAgent")}
                          required
                          error={errors.assignedTo}
                          touched={touched.assignedTo}
                          className="md:col-span-2"
                        >
                          <Field
                            as="select"
                            name="assignedTo"
                            disabled={false}
                            className={`${FIELD_CLASS} ${fieldErrorClass(errors.assignedTo, touched.assignedTo)}`}
                          >
                            <option value="">{t("ccAccountPlanSelectAgent")}</option>
                            {agentOptions.map((agent) => (
                              <option key={agent.userId} value={String(agent.userId)}>
                                {agent.name}
                              </option>
                            ))}
                          </Field>
                        </FormField>
                      )}

                      <FormField
                        label={t("ccAccountPlanTicketPriority")}
                        required
                        error={errors.ticketPriority}
                        touched={touched.ticketPriority}
                        className="md:col-span-2 md:max-w-xs"
                      >
                        <Field
                          as="select"
                          name="ticketPriority"
                          disabled={templateLocked}
                          className={`${FIELD_CLASS} ${fieldErrorClass(errors.ticketPriority, touched.ticketPriority)}`}
                        >
                          {(metadata?.priorities || []).map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </FormField>

                      <FormField
                        label={t("ccAccountPlanRemarksTemplate")}
                        className="md:col-span-2"
                      >
                        <Field
                          as="textarea"
                          name="remarksTemplate"
                          rows={3}
                          disabled={templateLocked}
                          className={TEXTAREA_CLASS}
                        />
                      </FormField>
                    </div>

                    {!isEditing && previewVisible && (
                      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <h4 className="text-sm font-semibold text-slate-900">
                            {t("ccAccountPlanPreviewTitle")}
                          </h4>
                          <span className="text-xs text-slate-500">
                            {t("ccAccountPlanPreviewTotal").replace(
                              "{amount}",
                              formatPreviewAmount(values.totalAmount)
                            )}
                          </span>
                        </div>
                        {previewRows.length === 0 ? (
                          <p className="text-sm text-slate-500">{t("ccAccountPlanPreviewEmpty")}</p>
                        ) : (
                          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="px-3 py-2">#</th>
                                  <th className="px-3 py-2">{t("ccAccountPlanDueDate")}</th>
                                  <th className="px-3 py-2">{t("ccAccountPlanReminderDate")}</th>
                                  <th className="px-3 py-2">{t("ccAccountPlanAmountColumn")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {previewRows.map((row) => (
                                  <tr key={row.installmentNumber} className="border-t border-slate-100">
                                    <td className="px-3 py-2">{row.installmentNumber}</td>
                                    <td className="px-3 py-2">{row.dueDate}</td>
                                    <td className="px-3 py-2">{row.reminderDate}</td>
                                    <td className="px-3 py-2 font-medium">
                                      {formatPreviewAmount(row.amount)} OMR
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-9 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      {t("ccCancel")}
                    </button>
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => handlePreview(values, validateForm, setTouched)}
                        className="h-9 rounded-xl border border-blue-200 bg-blue-50 px-4 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        {t("ccAccountPlanPreviewSchedule")}
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={saving}
                      className="h-9 rounded-xl bg-[#2B7FFF] px-4 text-sm font-semibold text-white hover:bg-[#1d6ef2] disabled:opacity-50"
                    >
                      {saving ? t("ccSaving") : t("ccSave")}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
    </CallCenterModal>
  );
};

export default AccountPlanFormModal;
